import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SlidersHorizontal, CalendarDays, Trash2, Plus } from "lucide-react";

export default function Settings() {
  const [th, setTh] = useState({ on_track: 0.95, at_risk: 0.85, high_risk: 0.7 });
  const [lockTimeout, setLockTimeout] = useState(30);
  const [holidays, setHolidays] = useState([]);
  const [nh, setNh] = useState({ date: "", name: "" });
  const [saving, setSaving] = useState(false);

  const loadHolidays = () => api.get("/holidays").then((r) => setHolidays(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => {
      if (r.data.delay_thresholds) setTh(r.data.delay_thresholds);
      if (r.data.lock_timeout_minutes) setLockTimeout(r.data.lock_timeout_minutes);
    }).catch(() => {});
    loadHolidays();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        delay_thresholds: th,
        lock_timeout_minutes: Number(lockTimeout),
      });
      toast.success("Pengaturan berhasil disimpan");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const addHoliday = async (e) => {
    e.preventDefault();
    if (!nh.date || !nh.name) return;
    try {
      await api.post("/holidays", nh);
      setNh({ date: "", name: "" });
      loadHolidays();
      toast.success("Hari libur ditambahkan");
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  const delHoliday = async (id) => {
    try {
      await api.delete(`/holidays/${id}`);
      loadHolidays();
      toast.success("Hari libur dihapus");
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">Konfigurasi Sistem</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Pengaturan</h1>
      </div>

      <Card className="p-6 space-y-5">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-heading font-semibold">Ambang Batas Risiko Keterlambatan</h3>
            <p className="text-xs text-muted-foreground">Persentase capaian target vs jadwal untuk penentuan status risiko proyek.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">On Track (Hijau &ge; %)</Label>
            <Input
              type="number"
              value={Math.round((th.on_track ?? 0.95) * 100)}
              onChange={(e) => setTh({ ...th, on_track: parseFloat(e.target.value) / 100 || 0 })}
            />
          </div>
          <div>
            <Label className="text-xs">At Risk (Kuning &ge; %)</Label>
            <Input
              type="number"
              value={Math.round((th.at_risk ?? 0.85) * 100)}
              onChange={(e) => setTh({ ...th, at_risk: parseFloat(e.target.value) / 100 || 0 })}
            />
          </div>
          <div>
            <Label className="text-xs">High Risk (Oranye &ge; %)</Label>
            <Input
              type="number"
              value={Math.round((th.high_risk ?? 0.7) * 100)}
              onChange={(e) => setTh({ ...th, high_risk: parseFloat(e.target.value) / 100 || 0 })}
            />
          </div>
        </div>

        <div className="pt-2 flex justify-end">
          <Button onClick={save} disabled={saving}>
            {saving ? "Menyimpan..." : "Simpan Pengaturan"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-heading font-semibold">Kalender Hari Libur Nasional</h3>
            <p className="text-xs text-muted-foreground">Hari Minggu & hari libur di sini tidak dihitung sebagai hari kerja.</p>
          </div>
        </div>

        <form onSubmit={addHoliday} className="grid grid-cols-1 md:grid-cols-[160px_1fr_auto] gap-2 items-end">
          <div>
            <Label className="text-xs">Tanggal</Label>
            <Input type="date" value={nh.date} onChange={(e) => setNh({ ...nh, date: e.target.value })} required />
          </div>
          <div>
            <Label className="text-xs">Nama</Label>
            <Input placeholder="mis. Idul Fitri" value={nh.name} onChange={(e) => setNh({ ...nh, name: e.target.value })} required />
          </div>
          <Button type="submit">
            <Plus className="h-4 w-4 mr-1" /> Tambah
          </Button>
        </form>

        <div className="divide-y border rounded-md max-h-64 overflow-y-auto">
          {holidays.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground text-center">Belum ada hari libur tersimpan.</p>
          ) : (
            holidays.map((h) => (
              <div key={h.id || h.date} className="flex items-center justify-between p-3 text-sm">
                <div>
                  <span className="font-mono font-medium">{h.date}</span>
                  <span className="ml-3 text-muted-foreground">{h.name}</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => delHoliday(h.id)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}
